import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { X, Clock, Calendar, Heart, HandHeart, CheckCircle2, CheckSquare } from "lucide-react";
import type { PracticeEntry } from "@/lib/db";
import { loadTimeslotConfig } from "@/hooks/usePractice";

const TIME_SLOTS_CONFIG = loadTimeslotConfig();

interface EntryDetailSheetProps {
  entry: PracticeEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  virtueNames?: string[];
  onFulfillPromise?: (id: string) => void;
  onFulfillVersprechen?: (id: string) => void;
}

// ── Status helpers ───────────────────────────────────────────────────────────

type AusgleichStatus = "done" | "today" | "overdue" | "planned" | null;
type VersprechenStatus = "kept" | "ends_today" | "awaiting" | "active" | null;

function getAusgleichStatus(entry: PracticeEntry): AusgleichStatus {
  if (!entry.versprechen) return null;
  if (entry.versprechenErfuellt) return "done";
  if (entry.versprechenBisDatum) {
    const now = new Date();
    const deadline = new Date(entry.versprechenBisDatum);
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    if (deadline < now) return "overdue";
    if (entry.versprechenBisDatum.slice(0, 10) === todayStr) return "today";
  }
  return "planned";
}

function getVersprechenStatus(entry: PracticeEntry): VersprechenStatus {
  if (!entry.promise) return null;
  if (entry.promiseErfuellt) return "kept";
  if (entry.promiseBisDatum) {
    const now = new Date();
    const deadline = new Date(entry.promiseBisDatum);
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    if (deadline < now) return "awaiting";
    if (entry.promiseBisDatum.slice(0, 10) === todayStr) return "ends_today";
  }
  return "active";
}

function AusgleichStatusBadge({ status }: { status: AusgleichStatus }) {
  if (!status) return null;
  if (status === "done")
    return <Badge className="text-xs bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30">Durchgeführt ✓</Badge>;
  if (status === "overdue")
    return <Badge variant="outline" className="text-xs text-gray-500 dark:text-muted-foreground">Noch ausstehend</Badge>;
  if (status === "today")
    return <Badge className="text-xs bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30">Heute</Badge>;
  return <Badge variant="outline" className="text-xs text-gray-500 dark:text-muted-foreground">Geplant</Badge>;
}

function VersprechenStatusBadge({ status }: { status: VersprechenStatus }) {
  if (!status) return null;
  if (status === "kept")
    return <Badge className="text-xs bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30">Eingehalten ✓</Badge>;
  if (status === "awaiting")
    return <Badge className="text-xs bg-cyan-500/20 text-cyan-700 dark:text-cyan-400 border-cyan-500/30">Zur Bestätigung</Badge>;
  if (status === "ends_today")
    return <Badge className="text-xs bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30">Endet heute</Badge>;
  return <Badge className="text-xs bg-vajra-blue/10 text-vajra-blue dark:text-vajra-cyan border-vajra-blue/20">Aktiv</Badge>;
}

// ── Component ────────────────────────────────────────────────────────────────

export function EntryDetailSheet({
  entry,
  open,
  onOpenChange,
  virtueNames,
  onFulfillPromise,
  onFulfillVersprechen,
}: EntryDetailSheetProps) {
  if (!entry) return null;

  const isPositive = entry.type === "positive";
  const ausgleichStatus = getAusgleichStatus(entry);
  const versprechenStatus = getVersprechenStatus(entry);

  // Time slot info
  const slot = TIME_SLOTS_CONFIG.find((ts) => ts.slot === entry.timeSlot);
  const timeRange = slot?.label.match(/\((.+)\)/)?.[1] ?? "";
  const slotLabel = slot ? `${slot.emoji} ${slot.labelShort}` : `Slot ${entry.timeSlot}`;

  // Timestamp
  const completedDate = new Date(entry.completedAt);
  const formattedDate = completedDate.toLocaleString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const formatDeadline = (iso: string) =>
    new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[85dvh] rounded-t-2xl border-t border-gray-200 dark:border-[#1E3A5F] p-0 bg-white dark:bg-[#0D1117] [&>button]:hidden"
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="h-1 w-10 rounded-full bg-gray-300 dark:bg-[#1E3A5F]" />
        </div>

        <ScrollArea className="h-[calc(85dvh-1.5rem)]">
          <div className="px-5 pb-10 pt-2 space-y-4">
            {/* Header */}
            <SheetHeader className="space-y-2 text-left">
              <div className="flex items-center justify-between gap-3">
                <Badge
                  variant="outline"
                  className={
                    isPositive
                      ? "text-emerald-600 dark:text-emerald-400 border-emerald-500/40"
                      : "text-rose-600 dark:text-rose-400 border-rose-500/40"
                  }
                >
                  {isPositive ? "✓ Positiv" : "✕ Negativ"}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground"
                  onClick={() => onOpenChange(false)}
                  aria-label="Schließen"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-1 text-xs text-gray-500 dark:text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  {slotLabel}
                  {timeRange && ` · ${timeRange}`}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {formattedDate}
                </span>
              </div>

              <SheetTitle className="sr-only">Eintrag-Details</SheetTitle>
            </SheetHeader>

            <Separator className="bg-gray-100 dark:bg-[#1E3A5F]" />

            {/* Main text */}
            <div
              className={`rounded-xl border p-4 ${
                isPositive
                  ? "border-emerald-500/20 bg-emerald-50/50 dark:bg-emerald-500/5"
                  : "border-rose-500/20 bg-rose-50/50 dark:bg-rose-500/5"
              }`}
            >
              <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed whitespace-pre-wrap">
                {entry.text}
              </p>
            </div>

            {/* Virtues */}
            {virtueNames && virtueNames.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-400 dark:text-muted-foreground uppercase tracking-wider">
                  Tugenden
                </p>
                <div className="flex flex-wrap gap-2">
                  {virtueNames.map((name) => (
                    <Badge
                      key={name}
                      variant="outline"
                      className={
                        isPositive
                          ? "border-emerald-500/30 text-emerald-700 dark:text-emerald-400"
                          : "border-rose-500/30 text-rose-700 dark:text-rose-400"
                      }
                    >
                      {name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Widmung (positive only) */}
            {isPositive && entry.widmung && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-400 dark:text-muted-foreground uppercase tracking-wider">
                  Widmung
                </p>
                <div className="rounded-xl bg-blue-500/5 border border-blue-500/20 px-4 py-3">
                  <p className="flex items-start gap-2 text-sm text-blue-700 dark:text-blue-400">
                    <Heart className="h-4 w-4 shrink-0 mt-0.5" />
                    {entry.widmung}
                  </p>
                </div>
              </div>
            )}

            {/* Ausgleichshandlung (negative only) */}
            {!isPositive && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-400 dark:text-muted-foreground uppercase tracking-wider">
                  Ausgleichshandlung
                </p>
                <div className="rounded-xl bg-amber-50/50 dark:bg-amber-500/5 border border-amber-500/20 px-4 py-3 space-y-2">
                  {entry.versprechen ? (
                    <p className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <HandHeart className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
                      {entry.versprechen}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-muted-foreground italic">
                      Keine Ausgleichshandlung eingetragen
                    </p>
                  )}

                  {entry.versprechenBisDatum && (
                    <p className="text-xs text-gray-500 dark:text-muted-foreground pl-6">
                      Bis: {formatDeadline(entry.versprechenBisDatum)}
                    </p>
                  )}

                  <div className="pl-6">
                    <AusgleichStatusBadge status={ausgleichStatus} />
                  </div>
                </div>

                {ausgleichStatus !== "done" && ausgleichStatus !== null && onFulfillPromise && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onFulfillPromise(entry.id);
                      onOpenChange(false);
                    }}
                    className="w-full border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                  >
                    <CheckSquare className="h-4 w-4 mr-2" />
                    Ausgleichshandlung durchgeführt
                  </Button>
                )}
              </div>
            )}

            {/* Versprechen (negative only) */}
            {!isPositive && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-400 dark:text-muted-foreground uppercase tracking-wider">
                  Versprechen
                </p>
                <div className="rounded-xl bg-vajra-blue/5 border border-vajra-blue/20 px-4 py-3 space-y-2">
                  {entry.promise ? (
                    <p className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-vajra-blue dark:text-vajra-cyan" />
                      {entry.promise}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-muted-foreground italic">
                      Kein Versprechen eingetragen
                    </p>
                  )}

                  {entry.promiseBisDatum && (
                    <p className="text-xs text-gray-500 dark:text-muted-foreground pl-6">
                      Bis: {formatDeadline(entry.promiseBisDatum)}
                    </p>
                  )}

                  <div className="pl-6">
                    <VersprechenStatusBadge status={versprechenStatus} />
                  </div>
                </div>

                {versprechenStatus !== "kept" && versprechenStatus !== null && onFulfillVersprechen && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onFulfillVersprechen(entry.id);
                      onOpenChange(false);
                    }}
                    className="w-full border-vajra-blue/30 text-vajra-blue dark:text-vajra-cyan hover:bg-vajra-blue/10"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Versprechen eingehalten ✓
                  </Button>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
